import * as React from 'react'

import i18n from 'modules/core/lib/i18n'
import { NavigationRoutesFn } from 'modules/core/lib/react-router'

import surveys from 'modules/surveys/store/collections/v2/surveys'

const CreateSurveyPage = React.lazy(() => import('modules/surveys/pages/Index/CreateSurveyPage'))
const NewSurveyPage = React.lazy(() => import('modules/surveys/pages/Index/NewSurveyPage'))
const NewWorkflowSurveyPage = React.lazy(
  () => import('modules/surveys/pages/Index/NewWorkflowSurveyPage')
)
const SurveysListPage = React.lazy(() => import('modules/surveys/pages/Index/SurveysListPage'))
const RootPage = React.lazy(() => import('modules/surveys/pages/RootPage'))
const AnalyticsPage = React.lazy(() => import('modules/surveys/pages/Show/Analytics/AnalyticsPage'))
const AnswerPage = React.lazy(() => import('modules/surveys/pages/Show/Answer/AnswerPage'))
const RedirectToAnswerPage = React.lazy(
  () => import('modules/surveys/pages/Show/Answer/RedirectToAnswerPage')
)
const AddSupervisorPage = React.lazy(
  () => import('modules/surveys/pages/Show/Configuration/AddSupervisorPage')
)
const SurveyConfigurationPage = React.lazy(
  () => import('modules/surveys/pages/Show/Configuration/ConfigurationPage')
)
const SurveyStartPage = React.lazy(
  () => import('modules/surveys/pages/Show/Configuration/SurveyStartPage')
)
const NewParticipantsPage = React.lazy(
  () => import('modules/surveys/pages/Show/Participants/NewParticipantsPage')
)
const ParticipantsPage = React.lazy(
  () => import('modules/surveys/pages/Show/Participants/ParticipantsPage')
)
const SurveyAddQuestionPage = React.lazy(
  () => import('modules/surveys/pages/Show/Questions/AddQuestionPage')
)
const ApplyQuestionsTemplatePage = React.lazy(
  () => import('modules/surveys/pages/Show/Questions/ApplyQuestionsTemplatePage')
)
const AddQuestionInCategoryPage = React.lazy(
  () => import('modules/surveys/pages/Show/Questions/Categories/AddQuestionInCategoryPage')
)
const SurveyAddCategoryPage = React.lazy(
  () => import('modules/surveys/pages/Show/Questions/Categories/SurveyAddCategoryPage')
)
const SurveyEditCategoryPage = React.lazy(
  () => import('modules/surveys/pages/Show/Questions/Categories/SurveyEditCategoryPage')
)
const SurveyRemoveCategoryPage = React.lazy(
  () => import('modules/surveys/pages/Show/Questions/Categories/SurveyRemoveCategoryPage')
)
const SurveyQuestionsPage = React.lazy(
  () => import('modules/surveys/pages/Show/Questions/QuestionsPage')
)
const SurveyArchiveQuestionPage = React.lazy(
  () => import('modules/surveys/pages/Show/Questions/SurveyArchiveQuestionPage')
)
const SurveyUnarchiveQuestionPage = React.lazy(
  () => import('modules/surveys/pages/Show/Questions/SurveyUnarchiveQuestionPage')
)
const ResponsesPage = React.lazy(() => import('modules/surveys/pages/Show/Responses/ResponsesPage'))
const ShowSurveyFormDefaultPageContainer = React.lazy(
  () => import('modules/surveys/pages/Show/ShowSurveyFormDefaultPageContainer')
)
const SurveyShowPage = React.lazy(() => import('modules/surveys/pages/Show/SurveyShowPage'))
const WriteSurveyResponsePage = React.lazy(
  () => import('modules/surveys/pages/WriteSurveyResponsePage')
)

const surveysRoutes: NavigationRoutesFn = (navigation) => {
  return [
    {
      path: navigation.engagement.surveys.path,
      element: <RootPage />,
      handle: {
        meta: ({ location, navigation }) => {
          // Hide breadcrumb when coming from smart_surveys to avoid duplicate
          const referrerIncludesSmartSurveys =
            typeof document !== 'undefined' &&
            document.referrer.includes(navigation.engagement.smart_surveys.path)
          const stateFromSmartSurveys =
            location.state &&
            typeof location.state === 'object' &&
            'fromSmartSurveys' in location.state &&
            location.state.fromSmartSurveys === true
          const isFromSmartSurveys = referrerIncludesSmartSurveys || stateFromSmartSurveys

          // Return empty title to hide this breadcrumb when coming from smart_surveys
          if (isFromSmartSurveys) {
            return {
              title: 'show_nothing',
            }
          }

          return {
            title: i18n.t('surveys.index.title'),
          }
        },
      },
      children: [
        {
          path: navigation.engagement.surveys.id.path,
          element: <SurveyShowPage />,
          loader: async ({ params }) => {
            const survey = surveys.get(params.id)
            if (survey) {
              return survey
            }
            return surveys.fetch({
              data: { ids: [params.id] },
            })
          },
          handle: {
            resource: true,
            meta: ({ params, navigation, location }) => {
              const survey = surveys.get(params.id)
              // Check if we came from smart_surveys context by checking referrer or location state
              const referrerIncludesSmartSurveys =
                typeof document !== 'undefined' &&
                document.referrer.includes(navigation.engagement.smart_surveys.path)
              const stateFromSmartSurveys =
                location.state &&
                typeof location.state === 'object' &&
                'fromSmartSurveys' in location.state &&
                location.state.fromSmartSurveys === true
              const isFromSmartSurveys = referrerIncludesSmartSurveys || stateFromSmartSurveys
              const ancestors = isFromSmartSurveys
                ? [
                    {
                      id: 0,
                      title: i18n.t('surveys.title'),
                      to: navigation.engagement.smart_surveys.classic_surveys.fullPath(),
                    },
                  ]
                : undefined
              return {
                title: survey ? survey.attributes.name : i18n.t('surveys.this_form'),
                ancestors,
              }
            },
          },
          children: [
            {
              index: true,
              element: <ShowSurveyFormDefaultPageContainer />,
            },
            {
              path: navigation.engagement.surveys.id.configuration.path,
              element: <SurveyConfigurationPage />,
              children: [
                {
                  path: navigation.engagement.surveys.id.configuration.start.path,
                  element: <SurveyStartPage />,
                },
                {
                  path: navigation.engagement.surveys.id.configuration.addManager.path,
                  element: <AddSupervisorPage />,
                },
              ],
            },
            {
              path: navigation.engagement.surveys.id.questions.path,
              element: <SurveyQuestionsPage />,
              children: [
                {
                  path: navigation.engagement.surveys.id.questions.archiveQuestionConfigId.path,
                  element: <SurveyArchiveQuestionPage />,
                },
                {
                  path: navigation.engagement.surveys.id.questions.unarchiveQuestionConfigId.path,
                  element: <SurveyUnarchiveQuestionPage />,
                },
                {
                  path: navigation.engagement.surveys.id.questions.questionGroupConfigIdAddQuestion
                    .path,
                  element: <SurveyAddQuestionPage />,
                },
                {
                  path: navigation.engagement.surveys.id.questions
                    .questionGroupConfigIdApplyTemplate.path,
                  element: <ApplyQuestionsTemplatePage />,
                },
                {
                  path: navigation.engagement.surveys.id.questions.questionGroupConfigIdAddCategory
                    .path,
                  element: <SurveyAddCategoryPage />,
                },
                {
                  path: navigation.engagement.surveys.id.questions
                    .questionGroupConfigIdCategoriesQuestionCategoryIdEditCategory.path,
                  element: <SurveyEditCategoryPage />,
                },
                {
                  path: navigation.engagement.surveys.id.questions
                    .questionGroupConfigIdCategoriesQuestionCategoryIdRemoveCategory.path,
                  element: <SurveyRemoveCategoryPage />,
                },
                {
                  path: navigation.engagement.surveys.id.questions
                    .questionGroupConfigIdCategoriesQuestionCategoryIdAddQuestion.path,
                  element: <AddQuestionInCategoryPage />,
                },
              ],
            },
            {
              path: navigation.engagement.surveys.id.participants.path,
              element: <ParticipantsPage />,
              children: [
                {
                  path: navigation.engagement.surveys.id.participants.new.path,
                  element: <NewParticipantsPage />,
                },
              ],
            },
            {
              path: navigation.engagement.surveys.id.responses.path,
              children: [
                {
                  index: true,
                  element: <ResponsesPage />,
                },
                {
                  path: navigation.engagement.surveys.id.responses.questionGroupId.path,
                  element: <WriteSurveyResponsePage />,
                },
              ],
            },
            {
              path: navigation.engagement.surveys.id.answer.path,
              element: <AnswerPage />,
              children: [
                {
                  path: navigation.engagement.surveys.id.answer.questionGroupId.path,
                  children: [
                    {
                      index: true,
                      element: <RedirectToAnswerPage />,
                    },
                  ],
                },
              ],
            },
            {
              path: navigation.engagement.surveys.id.analytics.path,
              element: <AnalyticsPage />,
            },
          ],
        },
        {
          path: '',
          element: <SurveysListPage />,
          children: [
            {
              path: navigation.engagement.surveys.new.path,
              element: <NewSurveyPage />,
            },
            {
              path: navigation.engagement.surveys.newWorkflowSurvey.path,
              element: <NewWorkflowSurveyPage />,
            },
            {
              path: navigation.engagement.surveys.creation.path,
              element: <CreateSurveyPage />,
            },
          ],
        },
      ],
    },
  ]
}

export default surveysRoutes
